/**
 * Death Effect System - manages death particles and animations
 */

import { Container, Graphics } from 'pixi.js';

/**
 * Death food colors - bright, vibrant colors for death drops
 */
export const DEATH_FOOD_COLORS = [
	0xffff00, // Bright yellow
	0xff9900, // Orange
	0xff00ff, // Magenta
	0x00ffff, // Cyan
	0xff6666, // Light red
	0x66ff66, // Light green
];

/**
 * Particle effect configuration
 */
export interface DeathParticle {
	x: number;
	y: number;
	vx: number;
	vy: number;
	radius: number;
	color: number;
	alpha: number;
	life: number;
}

/**
 * Death effect system - manages particles and animations
 */
export class DeathEffectSystem {
	private container: Container;
	private graphics: Graphics;
	private particles: DeathParticle[] = [];
	private active: boolean = false;

	constructor() {
		this.container = new Container();
		this.container.label = 'death-effects';
		this.graphics = new Graphics();
		this.container.addChild(this.graphics);
	}

	getContainer(): Container {
		return this.container;
	}

	/**
	 * Spawn death explosion at a position
	 */
	spawnExplosion(x: number, y: number, color: number, particleCount: number = 30): void {
		this.active = true;

		for (let i = 0; i < particleCount; i++) {
			const angle = (Math.PI * 2 * i) / particleCount + Math.random() * 0.5;
			const speed = 200 + Math.random() * 400;

			this.particles.push({
				x,
				y,
				vx: Math.cos(angle) * speed,
				vy: Math.sin(angle) * speed,
				radius: 4 + Math.random() * 8,
				color: DEATH_FOOD_COLORS[Math.floor(Math.random() * DEATH_FOOD_COLORS.length)] ?? color,
				alpha: 1,
				life: 0.5 + Math.random() * 0.5,
			});
		}
	}

	/**
	 * Update particles
	 */
	update(dt: number): void {
		if (!this.active) return;

		// Update particle positions and life
		this.particles = this.particles.filter(p => {
			p.x += p.vx * dt;
			p.y += p.vy * dt;
			p.vx *= 0.95; // Friction
			p.vy *= 0.95;
			p.life -= dt;
			p.alpha = Math.max(0, p.life * 2);
			return p.life > 0;
		});

		// Draw particles
		this.graphics.clear();
		for (const p of this.particles) {
			this.graphics.circle(p.x, p.y, p.radius * p.alpha);
			this.graphics.fill({ color: p.color, alpha: p.alpha });
		}

		// Deactivate if no particles left
		if (this.particles.length === 0) {
			this.active = false;
		}
	}

	destroy(): void {
		this.particles = [];
		this.graphics.destroy();
		this.container.destroy({ children: true });
	}
}
